import type * as React from 'react';
import { cn } from './lib/cn';

type DivProps = React.HTMLAttributes<HTMLDivElement>;

export interface CardProps extends DivProps {
  /**
   * `flush` tira a moldura no celular: o conteudo vaza ate as bordas da tela
   * sobre uma faixa branca, sem borda nem cantos. A partir de `sm:` ele volta
   * a ser cartao. Use em formulario e em lista, onde a moldura nao separa nada
   * de nada - mas a faixa ainda precisa se destacar do fundo da pagina.
   */
  variant?: 'default' | 'flush';
}

export function Card({ className, variant = 'default', ...props }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-card border border-border bg-card text-card-foreground',
        // So o primeiro bloco do cartao tem recuo em cima: o que vem depois de
        // um cabecalho ou de um conteudo ja esta espacado por ele. Assim um
        // cartao so com conteudo nao cola o texto na borda de cima.
        '[&>*+*]:pt-0',
        variant === 'flush' &&
          // O -mx-3 anula o recuo da pagina: a faixa encosta nas bordas da
          // tela, e o recuo volta por dentro para o texto nao colar nelas.
          '-mx-3 rounded-none border-0 border-y border-border [&>*]:px-3 sm:mx-0 sm:rounded-card sm:border sm:[&>*]:px-6',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: DivProps) {
  // Padding menor no celular: a tela estreita nao pode gastar 24px de cada lado.
  return <div className={cn('flex flex-col space-y-2 p-4 sm:p-6', className)} {...props} />;
}

export function CardTitle({ className, ...props }: DivProps) {
  return (
    <h3
      className={cn('font-semibold leading-none tracking-tight', className)}
      {...(props as React.HTMLAttributes<HTMLHeadingElement>)}
    />
  );
}

export function CardDescription({ className, ...props }: DivProps) {
  return (
    <p
      className={cn('text-sm text-muted-foreground', className)}
      {...(props as React.HTMLAttributes<HTMLParagraphElement>)}
    />
  );
}

export function CardContent({ className, ...props }: DivProps) {
  return <div className={cn('p-4 sm:p-6', className)} {...props} />;
}

export function CardFooter({ className, ...props }: DivProps) {
  return <div className={cn('flex items-center p-4 sm:p-6', className)} {...props} />;
}
